/**
 * Taco Slap — the rules, with no DOM in sight.
 *
 * A solo take on the chant-and-slap shedding game. The player holds a face-down pile.
 * Each card flips onto the central pile and the chant advances one word:
 * taco, cat, goat, cheese, pizza, taco, … The player must, within a short window:
 *   - tap the icon of the chant word, if the card does NOT match it ("say" the word);
 *   - slap the card, if the card IS the chant word (a match);
 *   - do the card's gesture, if it is a special card (gorilla / octopus / unicorn).
 * A correct slap or gesture sheds the whole central pile for good. Any wrong move, or
 * running out of time, sends the pile back under the player's deck plus a time penalty.
 * The round ends when the player's pile is empty; the score is the elapsed time.
 *
 * Everything is driven by a `now` (ms) the caller passes in, so it is testable and pause-able.
 */

export const CHANT = ['taco', 'cat', 'goat', 'cheese', 'pizza'] as const;
export type Word = (typeof CHANT)[number];
export const SPECIALS = ['gorilla', 'octopus', 'unicorn'] as const;
export type Special = (typeof SPECIALS)[number];
export type CardKind = Word | Special;

export type Action = { type: 'say'; word: Word } | { type: 'slap' } | { type: 'gesture'; special: Special };

export interface Rules {
  /** copies of each chant word in the deck */
  perWord: number;
  /** copies of each special card in the deck */
  perSpecial: number;
  /** ms allowed to answer a plain or matching card */
  windowMs: number;
  /** ms allowed to answer a special card (gestures take a beat longer) */
  specialWindowMs: number;
  /** ms added to the clock for a wrong move or a timeout */
  penaltyMs: number;
}

export const DEFAULT_RULES: Rules = {
  perWord: 6,
  perSpecial: 2,
  windowMs: 2000,
  specialWindowMs: 2600,
  penaltyMs: 2000,
};

export function isSpecial(k: CardKind): k is Special {
  return (SPECIALS as readonly string[]).includes(k);
}

/** the one correct answer for a card at a point in the chant */
export function expectedAction(card: CardKind, chant: number): Action {
  if (isSpecial(card)) return { type: 'gesture', special: card };
  return card === CHANT[chant] ? { type: 'slap' } : { type: 'say', word: CHANT[chant] };
}

export function sameAction(a: Action, b: Action): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'say' && b.type === 'say') return a.word === b.word;
  if (a.type === 'gesture' && b.type === 'gesture') return a.special === b.special;
  return true;
}

export function buildDeck(rules: Rules, rng: () => number = Math.random): CardKind[] {
  const deck: CardKind[] = [];
  for (const w of CHANT) for (let i = 0; i < rules.perWord; i++) deck.push(w);
  for (const s of SPECIALS) for (let i = 0; i < rules.perSpecial; i++) deck.push(s);
  // Fisher–Yates
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

export type Status = 'idle' | 'playing' | 'won';

export interface Outcome {
  ok: boolean;
  /** what the player did; 'timeout' when the window closed */
  did: Action | 'timeout';
  expected: Action;
  /** cards sent back under the deck (a mistake) */
  taken: number;
  /** cards shed for good (a correct slap/gesture) */
  shed: number;
  won: boolean;
}

export interface Snapshot {
  status: Status;
  card: CardKind | null;
  chant: number;
  deck: number;
  pile: number;
  shed: number;
  mistakes: number;
  /** total cards this round (deck + pile + shed) */
  total: number;
}

export class TacoSlap {
  readonly rules: Rules;
  private deck: CardKind[] = [];
  private pile: CardKind[] = [];
  private chant = 0;
  private card: CardKind | null = null;
  private cardAt = 0;
  private startAt = 0;
  private penalties = 0;
  private pausedTotal = 0;
  private pausedAt: number | null = null;
  private status: Status = 'idle';
  shed = 0;
  mistakes = 0;
  streak = 0;
  bestStreak = 0;

  private rng: () => number;

  constructor(rules: Partial<Rules> = {}, rng: () => number = Math.random) {
    this.rules = { ...DEFAULT_RULES, ...rules };
    this.rng = rng;
  }

  get total(): number {
    return this.deck.length + this.pile.length + this.shed;
  }

  snapshot(): Snapshot {
    return { status: this.status, card: this.card, chant: this.chant, deck: this.deck.length, pile: this.pile.length, shed: this.shed, mistakes: this.mistakes, total: this.total };
  }

  /** the word the player should be "saying" for the current card */
  get word(): Word {
    return CHANT[this.chant];
  }

  get currentCard(): CardKind | null {
    return this.card;
  }

  get expected(): Action | null {
    return this.card ? expectedAction(this.card, this.chant) : null;
  }

  start(now: number): void {
    this.deck = buildDeck(this.rules, this.rng);
    this.pile = [];
    this.chant = 0;
    this.shed = 0;
    this.mistakes = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.penalties = 0;
    this.pausedTotal = 0;
    this.pausedAt = null;
    this.startAt = now;
    this.status = 'playing';
    this.flip(now);
  }

  /** the running clock: wall time minus pauses plus penalties */
  elapsed(now: number): number {
    if (this.status === 'idle') return 0;
    const end = this.pausedAt ?? now;
    return Math.max(0, end - this.startAt - this.pausedTotal + this.penalties);
  }

  /** ms allowed for the current card */
  get windowMs(): number {
    return this.card && isSpecial(this.card) ? this.rules.specialWindowMs : this.rules.windowMs;
  }

  /** 1 → window just opened, 0 → out of time */
  remaining(now: number): number {
    if (this.status !== 'playing' || this.pausedAt !== null) return 1;
    return Math.max(0, 1 - (now - this.cardAt) / this.windowMs);
  }

  pause(now: number): void {
    if (this.status === 'playing' && this.pausedAt === null) this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.pausedAt !== null) {
      const gap = now - this.pausedAt;
      this.pausedTotal += gap;
      this.cardAt += gap; // the window picks up where it left off
      this.pausedAt = null;
    }
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  /** call every frame; returns an outcome when the window has closed */
  tick(now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null || !this.card) return null;
    if (now - this.cardAt < this.windowMs) return null;
    return this.resolve('timeout', now);
  }

  act(action: Action, now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null || !this.card) return null;
    return this.resolve(action, now);
  }

  private resolve(did: Action | 'timeout', now: number): Outcome {
    const expected = expectedAction(this.card!, this.chant);
    const ok = did !== 'timeout' && sameAction(did, expected);
    let taken = 0;
    let shed = 0;
    if (ok) {
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      if (expected.type === 'say') {
        this.chant = (this.chant + 1) % CHANT.length;
      } else {
        // a won pile is gone for good, and the chant starts over
        shed = this.pile.length;
        this.shed += shed;
        this.pile = [];
        this.chant = 0;
      }
    } else {
      this.mistakes++;
      this.streak = 0;
      this.penalties += this.rules.penaltyMs;
      // the pile goes under your deck, and the chant starts over
      taken = this.pile.length;
      this.deck.push(...this.pile);
      this.pile = [];
      this.chant = 0;
    }
    this.card = null;
    let won = false;
    if (this.deck.length === 0) {
      this.status = 'won';
      won = true;
    } else {
      this.flip(now);
    }
    return { ok, did, expected, taken, shed, won };
  }

  private flip(now: number): void {
    this.card = this.deck.shift()!;
    this.pile.push(this.card);
    this.cardAt = now;
  }
}

/** a tiny seeded RNG (mulberry32) for tests and replays */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export { formatTime } from '../../ui/format.ts';
